import React, { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { Plus, X } from 'lucide-react'
import { componentScope } from '../../lib/cssScope'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { TextField } from '@/components/ui/TextField'
import shellStyles from '../tpsm/SupplierFormModal.module.css'
import formStyles from '../../styles/components/assets/AssetFormModal.module.css'
import { assetTypeOptions, normalizeAssetType } from '../../constants/assetTaxonomy'

interface AddAssetCardProps {
  onAssetAdded?: () => void
}

interface User {
  id: string
  username: string
  email?: string
}

const AddAssetCard: React.FC<AddAssetCardProps> = ({ onAssetAdded }) => {
  const { t } = useI18n()
  const [modalOpen, setModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    ip: '',
    hostname: '',
    url: '',
    environment: 'PRODUCTION',
    description: '',
    autoDetect: false,
    type: 'OTHER',
    ownerId: '',
    criticality: '',
    os: '',
    patchLevel: '',
    edrInstalled: false,
    firewallEnabled: false,
    encryptionEnabled: false,
    riskScore: '',
    complianceScore: ''
  })

  useEffect(() => {
    if (modalOpen) {
      loadUsers()
    }
  }, [modalOpen])

  const loadUsers = async () => {
    setLoadingUsers(true)
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || ''
      const response = await fetch('/api/organizations/current/users', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setUsers(Array.isArray(data.users) ? data.users : [])
      } else {
        setUsers([])
      }
    } catch (err) {
      console.error('Failed to load users:', err)
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }

  const addTag = () => {
    const tag = tagInput.trim()
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag])
      setTagInput('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove))
  }

  const resolveOrgId = async (): Promise<string | undefined> => {
    const { getActiveOrgId } = await import('../../utils/orgUtils')
    const orgId = getActiveOrgId()
    if (orgId) return orgId
    const rawUser = localStorage.getItem('user')
    const userObj = rawUser ? (() => { try { return JSON.parse(rawUser) } catch { return null } })() : null
    const primary = userObj?.organizationsDetailed?.[0] ?? userObj?.organizations?.[0]
    if (primary && typeof primary === 'object' && primary.id) return String(primary.id)
    if (typeof primary === 'string') {
      const name = primary.trim()
      if (!name) return undefined
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || ''
        const res = await fetch('/api/admin/organizations', { headers: { Authorization: `Bearer ${token}` } })
        if (res.ok) {
          const list = await res.json()
          const arr = Array.isArray(list) ? list : (list?.organizations || list?.data || [])
          const org = arr.find((o: any) => (o?.name || o) === name)
          if (org?.id) return String(org.id)
        }
      } catch {}
      return undefined
    }
    // Fallback: fetch /auth/me to get organizationId or organizations
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || ''
      const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      if (res.ok) {
        const me = await res.json()
        const u = me?.user ?? me
        if (u?.organizationId) return String(u.organizationId)
        const first = u?.organizations?.[0]
        if (first?.id) return String(first.id)
        if (typeof first === 'string') {
          const listRes = await fetch('/api/admin/organizations', { headers: { Authorization: `Bearer ${token}` } })
          if (listRes.ok) {
            const list = await listRes.json()
            const arr = Array.isArray(list) ? list : (list?.organizations || list?.data || [])
            const org = arr.find((o: any) => (o?.name || o) === first)
            if (org?.id) return String(org.id)
          }
        }
      }
    } catch {}
    return undefined
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || ''
      const orgId = await resolveOrgId()

      if (!orgId) {
        throw new Error(t('assets.add.noOrganization', 'Organization not found'))
      }

      // Determine asset type and identifier
      let assetType = 'OTHER'
      let identifier = ''

      if (formData.autoDetect) {
        // Auto-detect based on what's filled
        if (formData.ip) {
          identifier = formData.ip
          assetType = 'NETWORK'
        } else if (formData.hostname) {
          identifier = formData.hostname
          assetType = 'NETWORK'
        } else if (formData.url) {
          identifier = formData.url
          assetType = 'APPLICATION'
        }
      } else {
        if (formData.ip) {
          identifier = formData.ip
          assetType = 'NETWORK'
        } else if (formData.hostname) {
          identifier = formData.hostname
          assetType = 'NETWORK'
        } else if (formData.url) {
          identifier = formData.url
          assetType = 'APPLICATION'
        }
      }

      if (!identifier) {
        throw new Error(t('assets.add.provideIdentifier', 'Please provide IP, Hostname, or URL'))
      }

      const payload: Record<string, unknown> = {
        name: formData.name || identifier,
        assetType: normalizeAssetType(formData.type || assetType),
        environment: formData.environment || 'PRODUCTION',
        organizationId: orgId,
      }
      if (formData.ip?.trim()) payload.ipAddress = formData.ip.trim()
      if (formData.hostname?.trim()) payload.hostname = formData.hostname.trim()
      if (formData.url?.trim()) payload.url = formData.url.trim()
      if (formData.description?.trim()) payload.description = formData.description.trim()
      const pn = (v: string | number) => { const n = typeof v === 'number' ? v : parseFloat(String(v)); return typeof n === 'number' && !isNaN(n) ? n : undefined }
      if (formData.ownerId && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(formData.ownerId))) payload.ownerId = formData.ownerId
      if (['CRITICAL','HIGH','MEDIUM','LOW'].includes(String(formData.criticality || ''))) payload.criticality = formData.criticality
      if (tags.length > 0) payload.tags = tags
      if (formData.os) payload.os = formData.os
      const patchNum = pn(formData.patchLevel); if (patchNum !== undefined) payload.patchLevel = patchNum
      const hasControlInput =
        !!formData.edrInstalled ||
        !!formData.firewallEnabled ||
        !!formData.encryptionEnabled ||
        !!formData.os ||
        patchNum !== undefined
      if (hasControlInput) {
        payload.postureExplicit = true
        payload.edrInstalled = !!formData.edrInstalled
        payload.firewallEnabled = !!formData.firewallEnabled
        payload.encryptionEnabled = !!formData.encryptionEnabled
      }
      const riskNum = pn(formData.riskScore); if (riskNum !== undefined) payload.riskScore = riskNum
      const compNum = pn(formData.complianceScore); if (compNum !== undefined) payload.complianceScore = compNum

      const response = await fetch('/api/assets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        const msg = data.message || data.error || t('assets.add.failed', 'Failed to add asset')
        throw new Error(msg)
      }

      // Reset form and close modal
      setFormData({
        name: '',
        ip: '',
        hostname: '',
        url: '',
        environment: 'PRODUCTION',
        description: '',
        autoDetect: false,
        type: 'OTHER',
        ownerId: '',
        criticality: '',
        os: '',
        patchLevel: '',
        edrInstalled: false,
        firewallEnabled: false,
        encryptionEnabled: false,
        riskScore: '',
        complianceScore: ''
      })
      setTags([])
      setTagInput('')
      setModalOpen(false)
      
      if (onAssetAdded) {
        onAssetAdded()
      }
    } catch (err: any) {
      setError(err.message || t('assets.add.error', 'An error occurred'))
      console.error('Failed to add asset:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className={`${componentScope('AddAssetCard')} ds-card flex h-full min-h-0 w-full min-w-0 flex-col rounded-[1.25rem] border border-[var(--ds-border,#e5e7eb)] p-4 shadow-[0_2px_10px_rgba(15,23,42,0.07)]`}>
        <div className="space-y-1 mb-4">
          <h3 className="text-base font-semibold text-[var(--ds-text,#111827)]">
            {t('assets.add.title', 'Add New Asset')}
          </h3>
        </div>
        <Button
          type="button"
          color="primary"
          fullWidth
          onClick={() => setModalOpen(true)}
          icon={<Plus className="h-4 w-4" />}
          iconPosition="end"
        >
          {t('assets.add.addAsset', 'Add Asset')}
        </Button>
      </div>

      {modalOpen && (
        <div
          className={shellStyles.backdrop}
          role="presentation"
          onClick={() => !loading && setModalOpen(false)}
        >
          <div
            className={`${shellStyles.dialog} ${formStyles.dialogWide}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-asset-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={shellStyles.header}>
              <h2 id="add-asset-modal-title" className={shellStyles.title}>
                {t('assets.add.title', 'Add New Asset')}
              </h2>
              <Button
                type="button"
                color="outline"
                iconOnly
                onClick={() => setModalOpen(false)}
                disabled={loading}
                className={shellStyles.closeBtn}
                aria-label={t('common.close', 'Close')}
                icon={<X className="h-5 w-5" aria-hidden />}
              >
                <span className="sr-only">{t('common.close', 'Close')}</span>
              </Button>
            </div>

            <form onSubmit={handleSubmit} className={shellStyles.form}>
              {error && (
                <div className={shellStyles.error} role="alert">
                  {error}
                </div>
              )}

              <div className={formStyles.stack}>
                <TextField
                  label={t('assets.add.name', 'Name')}
                  value={formData.name}
                  onChange={(v) => setFormData({ ...formData, name: v })}
                  placeholder={t('assets.add.namePlaceholder', 'Asset name')}
                  minWidth="100%"
                />

                <div className={formStyles.grid3}>
                  <TextField
                    label={t('assets.add.ip', 'IP')}
                    value={formData.ip}
                    onChange={(v) => setFormData({ ...formData, ip: v })}
                    placeholder="192.168.1.1"
                    minWidth="100%"
                  />
                  <TextField
                    label={t('assets.add.hostname', 'Hostname')}
                    value={formData.hostname}
                    onChange={(v) => setFormData({ ...formData, hostname: v })}
                    placeholder="example.com"
                    minWidth="100%"
                  />
                  <TextField
                    label={t('assets.add.url', 'URL')}
                    value={formData.url}
                    onChange={(v) => setFormData({ ...formData, url: v })}
                    placeholder="https://example.com"
                    minWidth="100%"
                  />
                </div>

                <label className={shellStyles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.autoDetect}
                    onChange={(e) => setFormData({ ...formData, autoDetect: e.target.checked })}
                    className={shellStyles.checkbox}
                  />
                  <span className={shellStyles.checkboxText}>{t('assets.add.autoDetect', 'Auto-detect')}</span>
                </label>

                <Select
                  label={t('assets.add.type', 'Type')}
                  minWidth="100%"
                  options={assetTypeOptions(t)}
                  value={formData.type}
                  onChange={(v) => v != null && setFormData({ ...formData, type: v })}
                  placeholder={t('assets.add.type', 'Type')}
                />

                <Select
                  label={t('assets.add.environment', 'Environment')}
                  minWidth="100%"
                  options={[
                    { value: 'PRODUCTION', label: t('assets.add.production', 'Production') },
                    { value: 'STAGING', label: t('assets.add.staging', 'Staging') },
                    { value: 'DEVELOPMENT', label: t('assets.add.development', 'Development') },
                    { value: 'TEST', label: t('assets.add.test', 'Test') },
                  ]}
                  value={formData.environment}
                  onChange={(v) => v != null && setFormData({ ...formData, environment: v })}
                  placeholder={t('assets.add.environment', 'Environment')}
                />

                <Select
                  label={t('assets.add.owner', 'Owner')}
                  minWidth="100%"
                  options={[
                    { value: '', label: t('assets.add.noOwner', 'No Owner') },
                    ...users.map((user) => ({
                      value: user.id,
                      label: `${user.username}${user.email ? ` (${user.email})` : ''}`,
                    })),
                  ]}
                  value={formData.ownerId}
                  onChange={(v) => setFormData({ ...formData, ownerId: v ?? '' })}
                  placeholder={t('assets.add.owner', 'Owner')}
                  disabled={loadingUsers}
                />

                <Select
                  label={t('assets.add.criticality', 'Criticality')}
                  minWidth="100%"
                  options={[
                    { value: '', label: t('assets.add.noCriticality', 'Not Set') },
                    { value: 'CRITICAL', label: t('assets.add.criticalityCritical', 'Critical') },
                    { value: 'HIGH', label: t('assets.add.criticalityHigh', 'High') },
                    { value: 'MEDIUM', label: t('assets.add.criticalityMedium', 'Medium') },
                    { value: 'LOW', label: t('assets.add.criticalityLow', 'Low') },
                  ]}
                  value={formData.criticality}
                  onChange={(v) => setFormData({ ...formData, criticality: v ?? '' })}
                  placeholder={t('assets.add.criticality', 'Criticality')}
                />

                <div>
                  <p className={formStyles.sectionLabel}>{t('assets.add.tags', 'Tags')}</p>
                  <div className={formStyles.tagRow}>
                    <div className={formStyles.tagRowField}>
                      <TextField
                        label={false}
                        value={tagInput}
                        onChange={setTagInput}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addTag()
                          }
                        }}
                        placeholder={t('assets.add.tagsPlaceholder', 'Type and press Enter to add tag')}
                        minWidth="100%"
                        aria-label={t('assets.add.tagsPlaceholder', 'Type and press Enter to add tag')}
                      />
                    </div>
                    <Button
                      type="button"
                      color="primary"
                      size="small"
                      onClick={addTag}
                      icon={<Plus className="h-4 w-4" />}
                      iconOnly
                      aria-label={t('assets.add.addTag', 'Add tag')}
                    >
                      <span className="sr-only">{t('assets.add.addTag', 'Add tag')}</span>
                    </Button>
                  </div>
                  {tags.length > 0 && (
                    <div className={formStyles.tagChips}>
                      {tags.map((tag) => (
                        <span key={tag} className={formStyles.tagChip}>
                          {tag}
                          <Button
                            type="button"
                            color="outline"
                            size="small"
                            onClick={() => removeTag(tag)}
                            className="!border-transparent !bg-transparent !p-0 !text-blue-600 !shadow-none hover:!text-blue-800"
                            iconOnly
                            icon={<X className="h-3 w-3" />}
                            aria-label={t('common.remove', 'Remove')}
                          >
                            <span className="sr-only">{t('common.remove', 'Remove')}</span>
                          </Button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className={shellStyles.grid2}>
                  <TextField
                    label={t('assets.add.os', 'Operating System')}
                    value={formData.os}
                    onChange={(v) => setFormData({ ...formData, os: v })}
                    placeholder="Linux, Windows, etc."
                    minWidth="100%"
                  />
                  <TextField
                    label={t('assets.add.patchLevel', 'Patch Level (%)')}
                    type="number"
                    min={0}
                    max={100}
                    value={formData.patchLevel}
                    onChange={(v) => setFormData({ ...formData, patchLevel: v })}
                    placeholder="0-100"
                    minWidth="100%"
                  />
                </div>

                <div className={shellStyles.grid2}>
                  <TextField
                    label={t('assets.add.riskScore', 'Risk Score')}
                    type="number"
                    min={0}
                    max={100}
                    value={formData.riskScore}
                    onChange={(v) => setFormData({ ...formData, riskScore: v })}
                    placeholder="0-100"
                    minWidth="100%"
                  />
                  <TextField
                    label={t('assets.add.complianceScore', 'Compliance Score')}
                    type="number"
                    min={0}
                    max={100}
                    value={formData.complianceScore}
                    onChange={(v) => setFormData({ ...formData, complianceScore: v })}
                    placeholder="0-100"
                    minWidth="100%"
                  />
                </div>

                <div>
                  <p className={formStyles.sectionLabel}>{t('assets.add.securityPosture', 'Security Posture')}</p>
                  <div className={formStyles.checkboxGroup}>
                    <label className={shellStyles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={formData.edrInstalled}
                        onChange={(e) => setFormData({ ...formData, edrInstalled: e.target.checked })}
                        className={shellStyles.checkbox}
                      />
                      <span className={shellStyles.checkboxText}>{t('assets.add.edrInstalled', 'EDR Installed')}</span>
                    </label>
                    <label className={shellStyles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={formData.firewallEnabled}
                        onChange={(e) => setFormData({ ...formData, firewallEnabled: e.target.checked })}
                        className={shellStyles.checkbox}
                      />
                      <span className={shellStyles.checkboxText}>{t('assets.add.firewallEnabled', 'Firewall Enabled')}</span>
                    </label>
                    <label className={shellStyles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={formData.encryptionEnabled}
                        onChange={(e) => setFormData({ ...formData, encryptionEnabled: e.target.checked })}
                        className={shellStyles.checkbox}
                      />
                      <span className={shellStyles.checkboxText}>{t('assets.add.encryptionEnabled', 'Encryption Enabled')}</span>
                    </label>
                  </div>
                </div>

                <TextField
                  label={t('assets.add.description', 'Description')}
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(v) => setFormData({ ...formData, description: v })}
                  placeholder={t('assets.add.descriptionPlaceholder', 'Optional description')}
                  minWidth="100%"
                />
              </div>

              <div className={shellStyles.footer}>
                <Button
                  type="button"
                  color="outline"
                  onClick={() => setModalOpen(false)}
                  disabled={loading}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={loading}
                  icon={loading ? undefined : <Plus className="h-4 w-4" aria-hidden />}
                  iconPosition="end"
                >
                  {loading ? t('assets.add.adding', 'Adding...') : t('assets.add.addAsset', 'Add Asset')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default AddAssetCard

